/**
 * App header: wordmark, primary nav, optional search and actions (cockatrice-ui `src/ui/AppHeader.jsx`, ported to
 * hyperscript; PLAN.md §0).
 * Phones: wordmark + actions on the first row, search full width below, nav scrolls sideways last.
 */
import './css/app-header.css';
import { mergeProps, splitProps } from 'solid-js';
import { el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';
import { uiText } from './text.ts';

export interface AppHeaderProps extends Attributes {
  /** Where the wordmark links. Default "#/library". */
  readonly href?: string | undefined;
  /** Replaces the wordmark text. Default "Cockatrice". */
  readonly wordmark?: Child;
  /** Main navigation, usually <NavLinks>. Wrapped in a <nav> landmark. */
  readonly nav?: Child;
  /** Search field. Full width on phones. */
  readonly search?: Child;
  /** Buttons on the right. At most one primary. */
  readonly actions?: Child;
  /** Accessible name of the nav landmark. Default "Main". */
  readonly label?: string | undefined;
  /** Stick to the top while scrolling. */
  readonly sticky?: boolean | undefined;
  /** Extra classes. */
  readonly class?: string | undefined;
}

export default function AppHeader(props: AppHeaderProps): Child {
  const [local, rest] = splitProps(props, ['class', 'href', 'wordmark', 'nav', 'search', 'actions', 'sticky', 'label']);
  return el(
    'header',
    mergeProps({ class: () => ['ui-app-header', local.sticky === true && 'ui-app-header--sticky', local.class].filter(Boolean).join(' ') }, rest),
    el('a', { class: 'ui-app-header__wordmark', href: () => local.href ?? '#/library' }, () => local.wordmark ?? uiText()('ui.app-header.wordmark')),
    when(
      () => Boolean(local.nav),
      () => el('nav', { class: 'ui-app-header__nav', 'aria-label': () => local.label ?? uiText()('ui.app-header.label') }, () => local.nav),
    ),
    when(
      () => Boolean(local.search),
      () => el('div', { class: 'ui-app-header__search' }, () => local.search),
    ),
    when(
      () => Boolean(local.actions),
      () => el('div', { class: 'ui-app-header__actions' }, () => local.actions),
    ),
  );
}
